const monthEndDay = (year: number, month: number): number => new Date(year, month, 0).getDate();

const pad2 = (value: number): string => String(value).padStart(2, '0');

const isValidYmd = (year: number, month: number, day: number): boolean => {
  if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1) return false;
  return day <= monthEndDay(year, month);
};

export type PeriodPrecision = 'month' | 'day';

export type ParsedPeriod = {
  year: number;
  month: number;
  day: number | null;
  precision: PeriodPrecision;
};

/** Só mm/aaaa (legado). Prefira parsePeriodInput para De/Até. */
export const parseMonthYear = (value: string): { month: number; year: number } | null => {
  const match = value.trim().match(/^(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const year = Number(match[2]);
  if (month < 1 || month > 12 || year < 1900 || year > 2100) return null;
  return { month, year };
};

/** dd/mm/aaaa (ex.: 05/05/1992) ou mm/aaaa (ex.: 01/2025). */
export const parsePeriodInput = (value: string): ParsedPeriod | null => {
  const trimmed = value.trim();
  const dayMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dayMatch) {
    const day = Number(dayMatch[1]);
    const month = Number(dayMatch[2]);
    const year = Number(dayMatch[3]);
    if (!isValidYmd(year, month, day)) return null;
    return { year, month, day, precision: 'day' };
  }
  const monthYear = parseMonthYear(trimmed);
  if (!monthYear) return null;
  return {
    year: monthYear.year,
    month: monthYear.month,
    day: null,
    precision: 'month',
  };
};

export const formatMonthYear = (month: number, year: number): string =>
  `${pad2(month)}/${year}`;

export const formatDayMonthYear = (day: number, month: number, year: number): string =>
  `${pad2(day)}/${pad2(month)}/${year}`;

export const normalizePeriodInput = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const parsed = parsePeriodInput(trimmed);
  if (!parsed) return trimmed;
  if (parsed.precision === 'day' && parsed.day != null) {
    return formatDayMonthYear(parsed.day, parsed.month, parsed.year);
  }
  return formatMonthYear(parsed.month, parsed.year);
};

export const normalizeMonthYearInput = (raw: string): string => normalizePeriodInput(raw);

export const toNativeMonthValue = (value: string): string => {
  const parsed = parsePeriodInput(value);
  if (!parsed) return '';
  return `${parsed.year}-${pad2(parsed.month)}`;
};

export const fromNativeMonthValue = (value: string): string => {
  const match = value.trim().match(/^(\d{4})-(\d{2})$/);
  if (!match) return '';
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return '';
  return formatMonthYear(month, year);
};

export const toNativeDateValue = (value: string): string => {
  const parsed = parsePeriodInput(value);
  if (!parsed) return '';
  const day = parsed.precision === 'day' && parsed.day != null ? parsed.day : 1;
  return `${parsed.year}-${pad2(parsed.month)}-${pad2(day)}`;
};

export const fromNativeDateValue = (value: string): string => {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!isValidYmd(year, month, day)) return '';
  return formatDayMonthYear(day, month, year);
};

export const getMonthYearBound = (
  value: string,
  role: 'start' | 'end',
): Date | null => {
  const parsed = parsePeriodInput(value);
  if (!parsed) return null;
  if (parsed.precision === 'day' && parsed.day != null) {
    return new Date(parsed.year, parsed.month - 1, parsed.day);
  }
  if (role === 'start') {
    return new Date(parsed.year, parsed.month - 1, 1);
  }
  const lastDay = monthEndDay(parsed.year, parsed.month);
  return new Date(parsed.year, parsed.month - 1, lastDay);
};

export const isValidPeriodInput = (value: string): boolean =>
  !value.trim() || parsePeriodInput(value) !== null;

export const isValidMonthYearInput = (value: string): boolean => isValidPeriodInput(value);
